/**
 * CommentOverlay — in-app collaborative review surface for INTERNAL STAFF.
 *
 * A floating action button (bottom-right) opens a slide-out panel with two
 * modes:
 *   (a) "This screen" — leave/triage structured comments against the current
 *       live route_path.
 *   (b) "Review run"  — pick an open review cycle and walk its ordered screens
 *       with per-screen sign-off + progress.
 *
 * Gating: the backend `comments_enabled` flag (from /auth/me) AND a reviewer
 * (staff) role — Channel Manager or ResetData Admin/Master — matching the
 * backend `require_reviewer` staff-gate (CHP-1287). Mounted inside the router's
 * root-route component (sibling of <Layout>) so router hooks resolve.
 *
 * All reads/writes go through TanStack Query against the hand-authored
 * client.ts `/review` methods; mutations invalidate the relevant keys.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { Camera, MessageSquare, PartyPopper } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { createPortal } from "react-dom";

import {
  ApiError,
  createReviewComment,
  deleteReviewComment,
  deleteReviewScreenshot,
  getReviewCycle,
  jiraBrowseUrl,
  listReviewComments,
  listReviewCycles,
  patchReviewComment,
  signoffScreen,
  unsignoffScreen,
  uploadReviewScreenshot,
  type JiraRef,
  type ReviewCategory,
  type ReviewCommentOut,
  type ReviewSeverity,
  type SignoffOutcome,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { router } from "@/router";

import { captureRegion, ensurePng, type CaptureResult } from "./capture";
import { deriveContext } from "./context";
import "./comment-overlay.css";
import {
  CATEGORIES,
  CATEGORY_LABEL,
  renderBody,
  SEVERITIES,
  SEVERITY_LABEL,
  TAG_SUGGESTIONS,
  timeAgo,
} from "./taxonomy";

type Mode = "screen" | "run";

// CHP-531: how often the live review queries (comments + cycle/run progress)
// re-poll so a resolve / sign-off / new comment made in a SECOND open session
// (another reviewer, a different profile) shows up here without a manual
// reload. ~15s — frequent enough to feel live, light enough for a tiny
// internal cohort.
const LIVE_REFETCH_MS = 15000;

/**
 * Normalise a route path for screen matching. The repo's `redirect_slashes=False`
 * discipline (and route-path drift being a documented trap) means a curated
 * screen authored with/without a trailing slash would otherwise silently fail
 * the exact `===` compare against the live pathname. Strip the trailing slash
 * (preserving the root "/") so both sides compare normalised.
 */
function normalizeRoutePath(p: string): string {
  if (!p) return p;
  return p.length > 1 ? p.replace(/\/+$/, "") : p;
}

// Reviewer (staff) roles allowed to use the review overlay — mirrors the
// backend `require_reviewer` staff-gate (CHP-1287, pen-test DR-4). Channel
// Partners (BDM/PO) and pending users are excluded. `me.role` is the backend
// Role enum and is null for a pending user, so the null-guard matters.
const REVIEWER_ROLES = new Set<string>(["channel_manager", "admin"]);

function isReviewerRole(role: string | null | undefined): boolean {
  return !!role && REVIEWER_ROLES.has(role);
}

/**
 * Public mount point. Renders nothing unless the backend `comments_enabled`
 * flag (surfaced on /auth/me) is set AND the signed-in user holds a reviewer
 * (staff) role — Channel Manager or ResetData Admin/Master.
 *
 * The role gate mirrors the backend: CHP-1287 (pen-test DR-4) tightened
 * `require_reviewer` so every /review/* endpoint is staff-only, superseding
 * ADR-0026's earlier any-active-user soft-launch intent. Without this gate a
 * partner session (wherever `comments_enabled` is on, e.g. DEV) mounts the
 * overlay, which immediately calls GET /review/comments and gets a 403 —
 * harmless but noisy (CHP-1300). Keeping the gate here means the panel + all
 * its /review queries never instantiate for a non-reviewer. This is a UX gate
 * only; `require_reviewer` remains the security boundary.
 */
export function CommentOverlay() {
  const { data: me } = useCurrentUser();
  // Gated on the BACKEND `comments_enabled` flag (surfaced on /auth/me), NOT a
  // build-time VITE boolean — `import.meta.env.X === "1"` is constant-folded to
  // false and tree-shaken before the docker entrypoint can substitute the
  // placeholder, so the overlay never renders on a built image. The backend is
  // the single source of truth (it also gates the /review endpoints). Also
  // gated on a reviewer/staff role so the frontend matches the CHP-1287
  // staff-gate — the two must agree or a non-reviewer 403s on every /review call.
  if (!me?.comments_enabled || !isReviewerRole(me.role)) return null;
  return <CommentOverlayInner />;
}

function CommentOverlayInner() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("screen");
  // The review run the panel is working in, shared across both tabs. Set in the
  // Run tab; read in the This-screen tab so comments made during a run attach to
  // it (cycle_id / screen_id) instead of being orphaned ad-hoc comments.
  const [activeCycleId, setActiveCycleId] = useState<string | null>(null);
  // While true the whole overlay is hidden (display:none) so it isn't baked into
  // a screenshot capture. State (incl. a pending screenshot) survives because the
  // tree stays mounted.
  const [capturing, setCapturing] = useState(false);
  // True while an app-level Radix dialog/drawer is open — the panel steps aside
  // (left) so it doesn't sit on top of a right-side drawer.
  const [appModalOpen, setAppModalOpen] = useState(false);
  // Which side the panel parks on when an app modal is open: the side of the
  // viewport with MORE free space beside the modal — so it clears a right-side
  // drawer (→ left), a left-side drawer (→ right), and sits in the larger margin
  // beside a centered dialog instead of always slamming left. CHP-518.
  const [panelSide, setPanelSide] = useState<"left" | "right">("right");

  // CHP-805: focus management + roving-tabindex refs for the panel chrome.
  const fabRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const screenTabRef = useRef<HTMLButtonElement>(null);
  const runTabRef = useRef<HTMLButtonElement>(null);
  const hasOpened = useRef(false);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // CHP-809: the matched route TEMPLATE id (e.g. "/deals/$dealId"), distinct
  // from the concrete pathname ("/deals/123"). Sent on comment create as
  // route_id so a reviewer can later match comments across concrete IDs of the
  // same template. The last match is the leaf route the reviewer is actually on.
  const routeId = useRouterState({
    select: (s) => s.matches.at(-1)?.routeId ?? null,
  });
  const { data: me, accessToken } = useCurrentUser();

  // Portal the overlay to document.body so it ESCAPES #root. An open Radix modal
  // (modal=true, used by every app dialog/drawer) sets aria-hidden +
  // pointer-events:none on #root and focus-traps inside itself — which makes an
  // overlay nested in #root inert. Living at body level + the guards below keep
  // the FAB/panel clickable, typeable, and on top of any app modal.
  const container = useMemo(() => {
    const el = document.createElement("div");
    el.className = "rcmt-portal";
    return el;
  }, []);
  useEffect(() => {
    document.body.appendChild(container);
    // Radix marks sibling body children aria-hidden when a modal opens — strip
    // it off our container so the overlay stays in the a11y tree.
    const unhide = () => {
      if (container.getAttribute("aria-hidden") !== null) {
        container.removeAttribute("aria-hidden");
      }
    };
    // Detect an open app dialog/drawer (any role=dialog NOT inside our portal).
    const detectAppModal = () => {
      const dialogs = document.querySelectorAll('[role="dialog"],[role="alertdialog"]');
      let appModal: HTMLElement | null = null;
      dialogs.forEach((d) => {
        if (!appModal && !container.contains(d)) appModal = d as HTMLElement;
      });
      setAppModalOpen(appModal !== null);
      if (appModal) {
        // Park the panel in the wider margin beside the modal (CHP-518): a
        // right-side drawer leaves room on the left, a left-side drawer on the
        // right, a centered dialog roughly even (tie → right, the default).
        const r = (appModal as HTMLElement).getBoundingClientRect();
        const leftGap = r.left;
        const rightGap = window.innerWidth - r.right;
        setPanelSide(rightGap >= leftGap ? "right" : "left");
      }
    };
    unhide();
    detectAppModal();
    const selfObs = new MutationObserver(unhide);
    selfObs.observe(container, { attributes: true, attributeFilter: ["aria-hidden"] });
    const bodyObs = new MutationObserver(detectAppModal);
    bodyObs.observe(document.body, { childList: true });
    // Radix's modal FocusScope traps focus via a document-level focusin listener;
    // stop it from yanking focus out of our panel inputs (capture phase).
    // Radix's modal FocusScope traps focus via document focusin/focusout
    // handlers: focusin refocuses when focus lands outside the dialog, and
    // focusout refocuses when focus LEAVES the dialog toward an outside element.
    // Stop both (capture phase, before Radix's bubble handlers) whenever the
    // focus is moving into/within our overlay, so the panel's inputs are
    // focusable/typeable while the modal stays open.
    const onFocusIn = (e: FocusEvent) => {
      if (container.contains(e.target as Node)) e.stopImmediatePropagation();
    };
    const onFocusOut = (e: FocusEvent) => {
      if (container.contains(e.relatedTarget as Node)) e.stopImmediatePropagation();
    };
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    // Radix DismissableLayer closes a modal on any pointerdown OUTSIDE it — and
    // the FAB/panel are outside every app modal. Stop the overlay's own pointer
    // events from reaching Radix's document dismiss handler, so clicking the FAB
    // doesn't close the modal you're trying to comment on. (stopPropagation, not
    // preventDefault, so clicks/focus inside the overlay still work.)
    const stopOutsideDismiss = (e: Event) => e.stopPropagation();
    container.addEventListener("pointerdown", stopOutsideDismiss, true);
    container.addEventListener("mousedown", stopOutsideDismiss, true);
    container.addEventListener("touchstart", stopOutsideDismiss, true);
    return () => {
      selfObs.disconnect();
      bodyObs.disconnect();
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      container.removeEventListener("pointerdown", stopOutsideDismiss, true);
      container.removeEventListener("mousedown", stopOutsideDismiss, true);
      container.removeEventListener("touchstart", stopOutsideDismiss, true);
      container.remove();
    };
  }, [container]);

  // Run a region-capture: hide our UI, let it paint, drag-select, snapshot.
  // Returns the discriminated CaptureResult (CHP-809) so callers can tell a
  // genuine failure from a cancel/too-small and surface an error accordingly.
  const requestCapture = useCallback(async (): Promise<CaptureResult> => {
    setCapturing(true);
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    const result = await captureRegion();
    setCapturing(false);
    return result;
  }, []);

  // Comment count for the current screen (drives the FAB badge). Always
  // enabled so the badge stays live even when the panel is closed.
  // CHP-531: poll on an interval so a resolve / new comment made in ANOTHER
  // open session (a second reviewer in a different profile) propagates here
  // without a manual reload. The FAB open-count tracks the polled data.
  const commentsQuery = useQuery({
    queryKey: ["review", "comments", pathname],
    queryFn: () => listReviewComments(accessToken!, { route_path: pathname }),
    enabled: !!accessToken,
    refetchInterval: LIVE_REFETCH_MS,
  });
  const openCount = useMemo(
    () => (commentsQuery.data ?? []).filter((c) => c.status === "open").length,
    [commentsQuery.data],
  );

  // Escape closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // CHP-805: move focus into the panel when it opens and restore it to the FAB
  // when it closes (skipping the initial mount so we never steal focus on load).
  useEffect(() => {
    if (open) {
      hasOpened.current = true;
      panelRef.current?.focus();
    } else if (hasOpened.current) {
      fabRef.current?.focus();
    }
  }, [open]);

  // CHP-805: ARIA tablist roving keyboard nav (two tabs) — Arrow/Home/End move
  // selection and focus between "This screen" and "Review run".
  const onTabKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight" || e.key === "ArrowDown" || e.key === "End") {
      e.preventDefault();
      setMode("run");
      runTabRef.current?.focus();
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp" || e.key === "Home") {
      e.preventDefault();
      setMode("screen");
      screenTabRef.current?.focus();
    }
  };

  // CHP-547 (Issue 1): restore trackpad / wheel scrolling inside the panel while
  // an app dialog/drawer is open. Those app modals are Radix `modal=true`, which
  // mounts react-remove-scroll; its sidecar adds a NON-PASSIVE, document-level
  // (bubble-phase) `wheel` listener that preventDefault()s scrolling for any
  // target OUTSIDE the active lock's container. This overlay portals to <body>
  // (escaping #root and that lock), so its scroll container (.rcmt-body) is
  // "outside" the lock → wheel scroll is cancelled and ONLY arrow keys work
  // (arrow keys bypass the wheel listener — exactly the reported symptom). We
  // can't register .rcmt-body as a shard of a lock we don't own, so we intercept
  // the wheel on .rcmt-body itself: because our listener is on the scroll
  // element it runs in the bubble phase BEFORE the document-level canceller, so
  // stopPropagation() keeps the canceller from ever firing. We then apply the
  // scroll manually (preventDefault to avoid a double-scroll). Guarded to only
  // intervene when an app modal is actually open (otherwise native scroll works)
  // and to NO-OP when the panel can't scroll further that way, letting the event
  // fall through to react-remove-scroll's normal overscroll handling.
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = bodyRef.current;
    if (!open || !appModalOpen || !el) return;
    const onWheel = (e: WheelEvent) => {
      const canScrollUp = el.scrollTop > 0;
      const canScrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      const goingDown = e.deltaY > 0;
      // Let the event through (no-op) at the scroll boundary so the underlying
      // lock keeps handling overscroll rather than us swallowing it.
      if ((goingDown && !canScrollDown) || (!goingDown && !canScrollUp)) return;
      // deltaMode: 0 = pixels (trackpad — the reported case), 1 = lines, 2 = pages.
      const step =
        e.deltaMode === 1
          ? e.deltaY * 16
          : e.deltaMode === 2
            ? e.deltaY * el.clientHeight
            : e.deltaY;
      e.preventDefault();
      e.stopPropagation();
      el.scrollTop += step;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [open, appModalOpen]);

  return createPortal(
    <div
      className={[
        "rcmt-root",
        capturing ? "rcmt-capturing" : "",
        appModalOpen ? `rcmt-app-modal-open rcmt-panel-${panelSide}` : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {!open && (
        <button
          ref={fabRef}
          type="button"
          className="rcmt-fab"
          onClick={() => setOpen(true)}
          aria-label={`Open review comments${openCount > 0 ? `, ${openCount} open` : ""}`}
        >
          <MessageSquare className="rcmt-fab-icon" aria-hidden size={16} />
          Review
          {openCount > 0 && <span className="rcmt-fab-count">{openCount}</span>}
        </button>
      )}

      {open && (
        <>
          <div className="rcmt-backdrop" aria-hidden />
          {/* Non-modal dialog (CHP-805): the page stays operable (reviewers
              comment against the live screen), so aria-modal is intentionally
              omitted — it would falsely tell AT the background is inert. */}
          <aside
            ref={panelRef}
            tabIndex={-1}
            className="rcmt-panel"
            role="dialog"
            aria-label="Review comments"
          >
            <header className="rcmt-header">
              <div>
                <h2 className="rcmt-title">Review</h2>
                <div className="rcmt-route">{pathname}</div>
              </div>
              <button
                type="button"
                className="rcmt-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <div
              className="rcmt-tabs"
              role="tablist"
              aria-label="Review mode"
              onKeyDown={onTabKeyDown}
            >
              <button
                ref={screenTabRef}
                type="button"
                role="tab"
                id="rcmt-tab-screen"
                aria-controls="rcmt-tabpanel"
                aria-selected={mode === "screen"}
                tabIndex={mode === "screen" ? 0 : -1}
                className="rcmt-tab"
                onClick={() => setMode("screen")}
              >
                This screen
              </button>
              <button
                ref={runTabRef}
                type="button"
                role="tab"
                id="rcmt-tab-run"
                aria-controls="rcmt-tabpanel"
                aria-selected={mode === "run"}
                tabIndex={mode === "run" ? 0 : -1}
                className="rcmt-tab"
                onClick={() => setMode("run")}
              >
                Review run
              </button>
            </div>

            <div
              className="rcmt-body"
              ref={bodyRef}
              role="tabpanel"
              id="rcmt-tabpanel"
              aria-labelledby={mode === "screen" ? "rcmt-tab-screen" : "rcmt-tab-run"}
            >
              {mode === "screen" ? (
                <ScreenMode
                  pathname={pathname}
                  routeId={routeId}
                  token={accessToken}
                  meId={me?.id ?? null}
                  isAdmin={me?.role === "admin"}
                  comments={commentsQuery.data ?? []}
                  loading={commentsQuery.isLoading}
                  activeCycleId={activeCycleId}
                  onRequestCapture={requestCapture}
                />
              ) : (
                <RunMode
                  token={accessToken}
                  onGo={() => setOpen(false)}
                  selectedId={activeCycleId}
                  setSelectedId={setActiveCycleId}
                />
              )}
            </div>
          </aside>
        </>
      )}
    </div>,
    container,
  );
}

// ---------------------------------------------------------------------------
// Mode (a): This screen
// ---------------------------------------------------------------------------

function ScreenMode({
  pathname,
  routeId,
  token,
  meId,
  isAdmin,
  comments,
  loading,
  activeCycleId,
  onRequestCapture,
}: {
  pathname: string;
  routeId: string | null;
  token: string | undefined;
  meId: string | null;
  isAdmin: boolean;
  comments: ReviewCommentOut[];
  loading: boolean;
  activeCycleId: string | null;
  onRequestCapture: () => Promise<CaptureResult>;
}) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState<ReviewSeverity>("change-needed");
  const [category, setCategory] = useState<ReviewCategory>("ux");
  const [tags, setTags] = useState<string[]>([]);
  // CHP-531: resolved comments are hidden from the active list by default —
  // a resolved item is "dealt with" and just adds noise. A toggle reveals them
  // (still rendered with the data-resolved styling) for audit/reopen.
  const [showResolved, setShowResolved] = useState(false);
  // Per-context-group expand/collapse overrides. Default is "current context
  // open, the rest collapsed"; a key here flips that group's state.
  const [groupOverrides, setGroupOverrides] = useState<Record<string, boolean>>({});
  // A pending screenshot (Blob) + its object-URL for the thumbnail preview.
  const [shot, setShot] = useState<Blob | null>(null);
  const [shotUrl, setShotUrl] = useState<string | null>(null);
  // True when a comment posted OK but its screenshot upload failed (so we don't
  // fail the whole submit → which would prompt a duplicate re-post).
  const [attachWarn, setAttachWarn] = useState(false);
  const [capturePending, setCapturePending] = useState(false);
  // Auto-derived sub-state descriptor (open dialog/drawer title, active tab,
  // wizard step, or page heading), editable by the reviewer. The panel sits OVER
  // the screen, so deriveContext() reads the underlying live state. Re-derived
  // when the route changes.
  const [context, setContext] = useState<string>(() => deriveContext());
  useEffect(() => {
    // CHP-809: defer the re-derive past a paint frame so the OUTGOING screen's
    // DOM (or a pending loader state) has been swapped out before deriveContext
    // reads the live DOM. Reading on the raw pathname change can otherwise
    // describe the previous screen until the reviewer manually re-derives. Two
    // rAFs to clear the transition the same way the capture layer does.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setContext(deriveContext()));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [pathname]);

  useEffect(() => {
    // Revoke the previous object URL when it changes / on unmount.
    return () => {
      if (shotUrl) URL.revokeObjectURL(shotUrl);
    };
  }, [shotUrl]);

  // CHP-532: a non-null message while an upload/paste image is being processed
  // or rejected (e.g. a non-image file). Cleared on success.
  const [imgError, setImgError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const setPendingShot = useCallback((blob: Blob) => {
    setShot(blob);
    setShotUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(blob);
    });
  }, []);

  const onCapture = useCallback(async () => {
    setCapturePending(true);
    const result = await onRequestCapture();
    setCapturePending(false);
    if (result.ok) {
      setImgError(null);
      setPendingShot(result.blob);
    } else if (result.reason === "error") {
      // CHP-809: a genuine capture failure (html2canvas threw / no blob) gets a
      // visible message; cancel and too-small still no-op quietly.
      setImgError("Couldn't capture that region. Try again.");
    }
  }, [onRequestCapture, setPendingShot]);

  // CHP-532: attach an uploaded or pasted image (designer: "screenshots for
  // things that can't be screenshotted, like a toast"). The endpoint is
  // PNG-only, so coerce to PNG before staging it as the pending screenshot.
  const attachImage = useCallback(
    async (blob: Blob) => {
      const png = await ensurePng(blob);
      if (!png) {
        setImgError("That file isn't a readable image.");
        return;
      }
      setImgError(null);
      setPendingShot(png);
    },
    [setPendingShot],
  );

  const onPickFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input so re-picking the same file fires onChange again.
      e.target.value = "";
      if (file) await attachImage(file);
    },
    [attachImage],
  );

  // CHP-532: paste an image straight into the comment form (Cmd/Ctrl-V).
  const onPaste = useCallback(
    (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith("image/"));
      const file = item?.getAsFile();
      if (file) {
        e.preventDefault();
        void attachImage(file);
      }
    },
    [attachImage],
  );

  const clearShot = useCallback(() => {
    setShot(null);
    setShotUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  // When a run is active, load it so we can (a) attach cycle_id to the comment
  // and (b) match the current route to a curated screen to also set screen_id.
  const activeCycleQuery = useQuery({
    queryKey: ["review", "cycle", activeCycleId],
    queryFn: () => getReviewCycle(token!, activeCycleId!),
    enabled: !!token && !!activeCycleId,
    refetchInterval: LIVE_REFETCH_MS,
  });
  const activeCycle = activeCycleQuery.data ?? null;
  const activeScreen =
    activeCycle?.screens.find(
      (s) => normalizeRoutePath(s.route_path) === normalizeRoutePath(pathname),
    ) ?? null;

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["review", "comments", pathname] });
    // Keep the run's per-screen counts and progress fresh when a comment lands
    // against the active run.
    if (activeCycleId) {
      void qc.invalidateQueries({ queryKey: ["review", "cycle", activeCycleId] });
      void qc.invalidateQueries({ queryKey: ["review", "cycles", "open"] });
    }
  }, [qc, pathname, activeCycleId]);

  const createMut = useMutation({
    mutationFn: async () => {
      const created = await createReviewComment(token!, {
        route_path: pathname,
        severity,
        category,
        tags,
        body: body.trim(),
        // CHP-809: send the matched route-template id so comments on two concrete
        // IDs of the same template share a route_id (cross-ID matching).
        ...(routeId ? { route_id: routeId } : {}),
        ...(context.trim() ? { context: context.trim() } : {}),
        ...(activeCycleId ? { cycle_id: activeCycleId } : {}),
        ...(activeScreen ? { screen_id: activeScreen.id } : {}),
      });
      // Attach the drawn/uploaded screenshot, if any. A failed attach must NOT
      // fail the (already-persisted) comment — that would show "could not post"
      // and prompt a duplicate re-post. Treat the comment as posted + warn.
      let shotFailed = false;
      if (shot) {
        try {
          await uploadReviewScreenshot(token!, created.id, shot);
        } catch {
          shotFailed = true;
        }
      }
      return { shotFailed };
    },
    onSuccess: ({ shotFailed }) => {
      setBody("");
      setTags([]);
      clearShot();
      setContext(deriveContext());
      setAttachWarn(shotFailed);
      invalidate();
    },
  });

  const toggleTag = (t: string) =>
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  // CHP-531: split the (already route-scoped) list into active vs resolved so
  // the resolved ones can be hidden behind the toggle.
  const openComments = useMemo(() => comments.filter((c) => c.status !== "resolved"), [comments]);
  const resolvedComments = useMemo(
    () => comments.filter((c) => c.status === "resolved"),
    [comments],
  );
  const visibleComments = showResolved ? comments : openComments;

  // Group the visible comments by their captured sub-state `context` (the open
  // dialog/drawer/tab/wizard-step that deriveContext() recorded when each was
  // left). A single route hosts many of these, which the URL can't distinguish,
  // so without this they all intermix into one flat stream — the "I'm on the
  // dashboard but seeing Help & Feedback comments" problem. The current live
  // context (the form's `context`) sorts first so "where I am now" is on top.
  const groups = useMemo(() => {
    const byKey = new Map<string, ReviewCommentOut[]>();
    for (const c of visibleComments) {
      const key = c.context?.trim() ?? "";
      const bucket = byKey.get(key);
      if (bucket) bucket.push(c);
      else byKey.set(key, [c]);
    }
    const current = context.trim();
    return [...byKey.entries()].sort(([ak, av], [bk, bv]) => {
      if (ak === current && bk !== current) return -1;
      if (bk === current && ak !== current) return 1;
      return bv.length - av.length || ak.localeCompare(bk);
    });
  }, [visibleComments, context]);

  return (
    <>
      {activeCycleId && (
        <div className="rcmt-runhint">
          {activeCycle ? (
            <>
              Attaching to run <strong>{activeCycle.title}</strong>
              {activeScreen ? " · this screen is in the run" : " · not a run screen"}
            </>
          ) : (
            "Attaching to the active run…"
          )}
        </div>
      )}
      <form
        className="rcmt-form"
        onPaste={onPaste}
        onSubmit={(e) => {
          e.preventDefault();
          if (!body.trim() || !token) return;
          createMut.mutate();
        }}
      >
        <div className="rcmt-field">
          <label className="rcmt-label" htmlFor="rcmt-context">
            Context <span className="rcmt-label-hint">(where on this screen — auto-detected)</span>
          </label>
          <div className="rcmt-context-row">
            <input
              id="rcmt-context"
              className="rcmt-input"
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="e.g. Closed-Won submit dialog · Step 3"
            />
            <button
              type="button"
              className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
              title="Re-detect from the current screen"
              onClick={() => setContext(deriveContext())}
            >
              ↻
            </button>
          </div>
        </div>

        <div className="rcmt-field">
          <span className="rcmt-label">Severity</span>
          <div className="rcmt-radios">
            {SEVERITIES.map((s) => (
              <label key={s.value} className="rcmt-radio" data-active={severity === s.value}>
                <input
                  type="radio"
                  name="rcmt-severity"
                  value={s.value}
                  checked={severity === s.value}
                  onChange={() => setSeverity(s.value)}
                />
                {s.label}
              </label>
            ))}
          </div>
        </div>

        <div className="rcmt-field">
          <label className="rcmt-label" htmlFor="rcmt-category">
            Category
          </label>
          <select
            id="rcmt-category"
            className="rcmt-select"
            value={category}
            onChange={(e) => setCategory(e.target.value as ReviewCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="rcmt-field">
          <span className="rcmt-label">Tags (optional)</span>
          <div className="rcmt-tagrow">
            {TAG_SUGGESTIONS.map((t) => (
              <button
                key={t}
                type="button"
                className="rcmt-tag"
                data-active={tags.includes(t)}
                onClick={() => toggleTag(t)}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="rcmt-field">
          <label className="rcmt-label" htmlFor="rcmt-body">
            Comment
          </label>
          <textarea
            id="rcmt-body"
            className="rcmt-textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What needs attention on this screen? **bold**, *italic*, `code` supported."
          />
        </div>

        <div className="rcmt-field">
          <span className="rcmt-label">Screenshot (optional)</span>
          {/* Hidden file input driven by the "Upload image" button (CHP-532). */}
          <input
            ref={fileInputRef}
            className="rcmt-sr-only"
            type="file"
            accept="image/*"
            aria-label="Upload image"
            onChange={onPickFile}
          />
          {shotUrl ? (
            <div className="rcmt-shot-preview">
              <img className="rcmt-shot-thumb" src={shotUrl} alt="Selected region" />
              <div className="rcmt-shot-actions">
                <button
                  type="button"
                  className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
                  onClick={onCapture}
                  disabled={capturePending}
                >
                  {capturePending ? "Selecting…" : "Retake"}
                </button>
                <button
                  type="button"
                  className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Replace with upload
                </button>
                <button type="button" className="rcmt-linkbtn" onClick={clearShot}>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="rcmt-shot-actions">
                <button
                  type="button"
                  className="rcmt-btn rcmt-btn-ghost"
                  onClick={onCapture}
                  disabled={capturePending}
                >
                  {capturePending ? (
                    "Selecting…"
                  ) : (
                    <>
                      <Camera aria-hidden size={14} /> Capture a region
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="rcmt-btn rcmt-btn-ghost"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload image
                </button>
              </div>
              <span className="rcmt-label-hint">…or paste an image into this form.</span>
            </>
          )}
          {imgError && <div className="rcmt-error">{imgError}</div>}
        </div>

        <button
          type="submit"
          className="rcmt-btn rcmt-btn-primary"
          disabled={!body.trim() || !token || createMut.isPending}
        >
          {createMut.isPending ? "Posting…" : "Post comment"}
        </button>
        {createMut.isError && <div className="rcmt-error">Could not post comment. Try again.</div>}
        {attachWarn && (
          <div className="rcmt-warning">
            Comment posted, but the image couldn’t attach (it may be too large). You can add it from
            the comment below.
          </div>
        )}
      </form>

      {loading ? (
        <div className="rcmt-empty">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="rcmt-empty">No comments on this screen yet.</div>
      ) : (
        <>
          {resolvedComments.length > 0 && (
            <button
              type="button"
              className="rcmt-resolved-toggle"
              aria-pressed={showResolved}
              onClick={() => setShowResolved((v) => !v)}
            >
              {showResolved
                ? `Hide resolved (${resolvedComments.length})`
                : `Show resolved (${resolvedComments.length})`}
            </button>
          )}
          {visibleComments.length === 0 ? (
            <div className="rcmt-empty">No open comments on this screen.</div>
          ) : groups.length <= 1 ? (
            // Only one sub-state on this screen → no need for group chrome.
            <div className="rcmt-list">
              {visibleComments.map((c) => (
                <CommentCard
                  key={c.id}
                  comment={c}
                  token={token}
                  meId={meId}
                  isAdmin={isAdmin}
                  onChanged={invalidate}
                  onRequestCapture={onRequestCapture}
                />
              ))}
            </div>
          ) : (
            <div className="rcmt-groups">
              {groups.map(([key, list]) => {
                const isCurrent = key === context.trim();
                const open = groupOverrides[key] ?? isCurrent;
                return (
                  <section
                    className="rcmt-group"
                    key={key || "__none__"}
                    data-current={isCurrent || undefined}
                  >
                    <button
                      type="button"
                      className="rcmt-group-header"
                      aria-expanded={open}
                      onClick={() => setGroupOverrides((o) => ({ ...o, [key]: !open }))}
                    >
                      <span className="rcmt-group-chevron" aria-hidden="true">
                        {open ? "▾" : "▸"}
                      </span>
                      <span className="rcmt-group-label">{key || "This screen"}</span>
                      {isCurrent && <span className="rcmt-group-badge">this screen</span>}
                      <span className="rcmt-group-count">{list.length}</span>
                    </button>
                    {open && (
                      <div className="rcmt-list">
                        {list.map((c) => (
                          <CommentCard
                            key={c.id}
                            comment={c}
                            token={token}
                            meId={meId}
                            isAdmin={isAdmin}
                            onChanged={invalidate}
                            onRequestCapture={onRequestCapture}
                            hideContext
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
}

// CHP-509: a small chip linking to the Jira issue this comment was exported to.
// Real keys link out (`<base>/browse/<key> ↗`); synthetic dev keys (CHP-DEV-*)
// have no resolvable issue, so they render as a plain, unlinked chip.
function ExportedTicketChip({ ticketRef }: { ticketRef: string }) {
  const href = jiraBrowseUrl(ticketRef);
  if (!href) {
    return (
      <span className="rcmt-export-chip" title="Exported to Jira (dev / synthetic key)">
        {ticketRef}
      </span>
    );
  }
  return (
    <a
      className="rcmt-export-chip rcmt-export-chip-link"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${ticketRef} in Jira`}
      onClick={(e) => e.stopPropagation()}
    >
      {ticketRef} ↗
    </a>
  );
}

function CommentCard({
  comment,
  token,
  meId,
  isAdmin,
  onChanged,
  onRequestCapture,
  hideContext = false,
}: {
  comment: ReviewCommentOut;
  token: string | undefined;
  meId: string | null;
  isAdmin: boolean;
  onChanged: () => void;
  onRequestCapture: () => Promise<CaptureResult>;
  // When the card is rendered under a context group header, the "↳ context"
  // line would just repeat the header, so suppress it.
  hideContext?: boolean;
}) {
  const resolved = comment.status === "resolved";
  const [shotBusy, setShotBusy] = useState(false);
  // CHP-532: upload/paste an image onto THIS existing comment.
  const [shotError, setShotError] = useState<string | null>(null);
  const cardFileRef = useRef<HTMLInputElement>(null);

  const toggleMut = useMutation({
    mutationFn: () =>
      patchReviewComment(token!, comment.id, {
        status: resolved ? "open" : "resolved",
      }),
    onSuccess: onChanged,
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteReviewComment(token!, comment.id),
    onSuccess: onChanged,
  });

  const mine = !!meId && comment.author_id === meId;
  // Who may delete this comment: the author always, plus admins (matches the
  // backend rule `is_author OR role == admin`). The control is shown only when
  // the user can actually delete, so non-admins don't get a button that 403s on
  // someone else's comment. The inline 403 error below remains as a backstop.
  const canDelete = mine || isAdmin;
  // Author/admin may also EDIT content — the backend PATCH allows
  // body/severity/category/tags for is_author OR admin (same gate as delete).
  const canEdit = mine || isAdmin;
  const [editing, setEditing] = useState(false);
  const [eBody, setEBody] = useState(comment.body);
  const [eSeverity, setESeverity] = useState<ReviewSeverity>(comment.severity as ReviewSeverity);
  const [eCategory, setECategory] = useState<ReviewCategory>(comment.category as ReviewCategory);
  const [eTags, setETags] = useState<string[]>(comment.tags);
  const beginEdit = () => {
    // Re-seed the editor from the current comment each time (a sibling refetch
    // may have changed it since mount).
    setEBody(comment.body);
    setESeverity(comment.severity as ReviewSeverity);
    setECategory(comment.category as ReviewCategory);
    setETags(comment.tags);
    setEditing(true);
  };
  const toggleETag = (t: string) =>
    setETags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  const editMut = useMutation({
    mutationFn: () =>
      patchReviewComment(token!, comment.id, {
        body: eBody.trim(),
        severity: eSeverity,
        category: eCategory,
        tags: eTags,
      }),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
  });

  // Capture a region and upload it to THIS comment (add or replace). The capture
  // hides the whole overlay so it isn't in the shot, then it returns.
  const onReplaceShot = async () => {
    if (!token) return;
    setShotBusy(true);
    try {
      const result = await onRequestCapture();
      if (result.ok) {
        setShotError(null);
        await uploadReviewScreenshot(token, comment.id, result.blob);
        onChanged();
      } else if (result.reason === "error") {
        // CHP-809: surface a genuine capture failure; cancel/too-small stay quiet.
        setShotError("Couldn't capture that region. Try again.");
      }
    } finally {
      setShotBusy(false);
    }
  };

  const onRemoveShot = async () => {
    if (!token) return;
    setShotBusy(true);
    try {
      await deleteReviewScreenshot(token, comment.id);
      onChanged();
    } finally {
      setShotBusy(false);
    }
  };

  // CHP-532: coerce an uploaded/pasted image to PNG and attach it to THIS
  // comment, reusing the same upload path as the region-capture Replace.
  const uploadImageBlob = async (blob: Blob) => {
    if (!token) return;
    const png = await ensurePng(blob);
    if (!png) {
      setShotError("That file isn't a readable image.");
      return;
    }
    setShotError(null);
    setShotBusy(true);
    try {
      await uploadReviewScreenshot(token, comment.id, png);
      onChanged();
    } catch (err) {
      // CHP-532 makes oversized PNGs the common case (full-res → lossless PNG
      // can exceed the 8MB cap) — surface it instead of silently doing nothing.
      setShotError(
        err instanceof ApiError && err.status === 413
          ? "Couldn't attach that image — it may be too large (max 8MB)."
          : "Couldn't attach that image. Try again.",
      );
    } finally {
      setShotBusy(false);
    }
  };

  const onCardPickFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) await uploadImageBlob(file);
  };

  const onCardPaste = (e: ClipboardEvent) => {
    // Only the author/admin (who may attach) handles a paste; others fall through.
    if (!mine) return;
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith("image/"));
    const file = item?.getAsFile();
    if (file) {
      e.preventDefault();
      void uploadImageBlob(file);
    }
  };

  return (
    <div className="rcmt-comment" data-resolved={resolved} onPaste={onCardPaste}>
      {/* Hidden file input for the card's "Upload" action (CHP-532). */}
      {mine && (
        <input
          ref={cardFileRef}
          className="rcmt-sr-only"
          type="file"
          accept="image/*"
          aria-label="Upload image to comment"
          onChange={onCardPickFile}
        />
      )}
      <div className="rcmt-comment-head">
        <span className="rcmt-chip" data-sev={comment.severity}>
          {SEVERITY_LABEL[comment.severity]}
        </span>
        <span className="rcmt-chip rcmt-chip-cat">{CATEGORY_LABEL[comment.category]}</span>
        <span className="rcmt-author">{comment.author_name ?? "Unknown"}</span>
        <span className="rcmt-time">· {timeAgo(comment.created_at)}</span>
        {comment.exported_ticket_ref && (
          <ExportedTicketChip ticketRef={comment.exported_ticket_ref} />
        )}
      </div>
      {comment.context && !hideContext && (
        <div className="rcmt-comment-context" title="Where on the screen this was left">
          ↳ {comment.context}
        </div>
      )}
      {editing ? (
        <form
          className="rcmt-edit"
          onSubmit={(e) => {
            e.preventDefault();
            if (!eBody.trim() || !token) return;
            editMut.mutate();
          }}
        >
          <div className="rcmt-radios">
            {SEVERITIES.map((s) => (
              <label key={s.value} className="rcmt-radio" data-active={eSeverity === s.value}>
                <input
                  type="radio"
                  name={`rcmt-edit-sev-${comment.id}`}
                  value={s.value}
                  checked={eSeverity === s.value}
                  onChange={() => setESeverity(s.value)}
                />
                {s.label}
              </label>
            ))}
          </div>
          <select
            className="rcmt-select"
            aria-label="Category"
            value={eCategory}
            onChange={(e) => setECategory(e.target.value as ReviewCategory)}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <div className="rcmt-tagrow">
            {TAG_SUGGESTIONS.map((t) => (
              <button
                key={t}
                type="button"
                className="rcmt-tag"
                data-active={eTags.includes(t)}
                onClick={() => toggleETag(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <textarea
            className="rcmt-textarea"
            aria-label="Edit comment"
            value={eBody}
            onChange={(e) => setEBody(e.target.value)}
          />
          <div className="rcmt-comment-actions">
            <button
              type="submit"
              className="rcmt-btn rcmt-btn-primary rcmt-btn-xs"
              disabled={!eBody.trim() || !token || editMut.isPending}
            >
              {editMut.isPending ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
              onClick={() => setEditing(false)}
              disabled={editMut.isPending}
            >
              Cancel
            </button>
          </div>
          {editMut.isError && <div className="rcmt-error">Could not save. Try again.</div>}
        </form>
      ) : (
        <div
          className="rcmt-comment-body"
          // Safe: renderBody escapes HTML before applying its tiny inline ruleset.
          dangerouslySetInnerHTML={{ __html: renderBody(comment.body) }}
        />
      )}
      {comment.screenshot_url ? (
        <div className="rcmt-comment-shot">
          <a
            className="rcmt-shot-link"
            href={comment.screenshot_url}
            target="_blank"
            rel="noreferrer"
            title="Open screenshot"
          >
            <img
              className="rcmt-shot-thumb"
              src={comment.screenshot_url}
              alt="Attached screenshot"
            />
          </a>
          {mine && (
            <div className="rcmt-shot-actions">
              <button
                type="button"
                className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
                onClick={onReplaceShot}
                disabled={shotBusy}
              >
                {shotBusy ? "Working…" : "Replace"}
              </button>
              <button
                type="button"
                className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
                onClick={() => cardFileRef.current?.click()}
                disabled={shotBusy}
              >
                Upload
              </button>
              <button
                type="button"
                className="rcmt-linkbtn"
                onClick={onRemoveShot}
                disabled={shotBusy}
              >
                Remove
              </button>
            </div>
          )}
        </div>
      ) : (
        mine && (
          <div className="rcmt-shot-actions rcmt-shot-add">
            <button
              type="button"
              className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
              onClick={onReplaceShot}
              disabled={shotBusy}
            >
              {shotBusy ? (
                "Working…"
              ) : (
                <>
                  <Camera aria-hidden size={14} /> Add screenshot
                </>
              )}
            </button>
            <button
              type="button"
              className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
              onClick={() => cardFileRef.current?.click()}
              disabled={shotBusy}
            >
              Upload image
            </button>
          </div>
        )
      )}
      {mine && shotError && <div className="rcmt-error">{shotError}</div>}
      {!editing && comment.tags.length > 0 && (
        <div className="rcmt-comment-tags">
          {comment.tags.map((t) => (
            <span key={t} className="rcmt-comment-tag">
              {t}
            </span>
          ))}
        </div>
      )}
      {!editing && (
        <div className="rcmt-comment-actions">
          <button
            type="button"
            className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
            disabled={!token || toggleMut.isPending}
            onClick={() => toggleMut.mutate()}
          >
            {toggleMut.isPending
              ? resolved
                ? "Reopening…"
                : "Resolving…"
              : resolved
                ? "Reopen"
                : "Resolve"}
          </button>
          {canEdit && (
            <button
              type="button"
              className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
              onClick={beginEdit}
              title={mine ? "Edit your comment" : "Edit (admin)"}
            >
              Edit
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
              disabled={!token || deleteMut.isPending}
              onClick={() => deleteMut.mutate()}
              title={mine ? "Delete your comment" : "Delete (admin)"}
            >
              Delete
            </button>
          )}
        </div>
      )}
      {/* CHP-809: a failed resolve/reopen surfaces inline (matching create / edit
          / delete) instead of silently reverting on the next ~15s refetch. */}
      {!editing && toggleMut.isError && (
        <div className="rcmt-error">
          {resolved ? "Could not reopen. Try again." : "Could not resolve. Try again."}
        </div>
      )}
      {!editing && deleteMut.isError && (
        <div className="rcmt-error">
          {deleteMut.error instanceof ApiError && deleteMut.error.status === 403
            ? "You can't delete this comment."
            : "Could not delete. Try again."}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mode (b): Review run
// ---------------------------------------------------------------------------

function RunMode({
  token,
  onGo,
  selectedId,
  setSelectedId,
}: {
  token: string | undefined;
  onGo: () => void;
  selectedId: string | null;
  setSelectedId: (id: string) => void;
}) {
  const cyclesQuery = useQuery({
    queryKey: ["review", "cycles", "open"],
    queryFn: () => listReviewCycles(token!, { status: "open" }),
    enabled: !!token,
    refetchInterval: LIVE_REFETCH_MS,
  });

  // Memoised so the effect dep is stable across renders (a fresh `[]` each
  // render would re-fire the effect — react-hooks/exhaustive-deps).
  const cycles = useMemo(() => cyclesQuery.data ?? [], [cyclesQuery.data]);
  // Default the picker to the first open cycle once loaded.
  useEffect(() => {
    if (!selectedId && cycles.length > 0) setSelectedId(cycles[0]!.id);
  }, [cycles, selectedId, setSelectedId]);

  if (cyclesQuery.isLoading) return <div className="rcmt-empty">Loading review runs…</div>;
  if (cycles.length === 0) return <div className="rcmt-empty">No open review runs right now.</div>;

  return (
    <>
      <div className="rcmt-field">
        <label className="rcmt-label" htmlFor="rcmt-cycle">
          Review run
        </label>
        <select
          id="rcmt-cycle"
          className="rcmt-select"
          value={selectedId ?? ""}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {cycles.map((c) => (
            <option key={c.id} value={c.id}>
              {c.section} — {c.title} ({c.my_signed_off_count}/{c.screen_count})
            </option>
          ))}
        </select>
      </div>
      {selectedId && <CycleScreens cycleId={selectedId} token={token} onGo={onGo} />}
    </>
  );
}

function CycleScreens({
  cycleId,
  token,
  onGo,
}: {
  cycleId: string;
  token: string | undefined;
  onGo: () => void;
}) {
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ["review", "cycle", cycleId],
    queryFn: () => getReviewCycle(token!, cycleId),
    enabled: !!token,
    refetchInterval: LIVE_REFETCH_MS,
  });

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["review", "cycle", cycleId] });
    void qc.invalidateQueries({ queryKey: ["review", "cycles", "open"] });
  }, [qc, cycleId]);

  if (detailQuery.isLoading) return <div className="rcmt-empty">Loading screens…</div>;
  const cycle = detailQuery.data;
  if (!cycle) return <div className="rcmt-empty">Could not load this run.</div>;

  const screens = [...cycle.screens].sort((a, b) => a.position - b.position);
  const total = screens.length;
  const done = screens.filter((s) => s.my_outcome === "done").length;
  const notComplete = screens.filter((s) => s.my_outcome === "not_complete").length;
  // CHP-533: "cannot check" is a third, neutral kick-back — reviewed but
  // un-checkable (blocked by another bug). Like not_complete it does NOT count
  // toward done, but it reads distinctly in the progress line.
  const cannotCheck = screens.filter((s) => s.my_outcome === "cannot_check").length;
  const toGo = total - done - notComplete - cannotCheck;
  // The run is "done" only when every screen is marked done — a not-complete or
  // cannot-check screen is reviewed but explicitly NOT finished, so it doesn't
  // count.
  const allDone = total > 0 && done === total;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  return (
    <>
      <div className="rcmt-progress">
        <div className="rcmt-progress-head">
          <span>
            {done} done
            {notComplete > 0 ? ` · ${notComplete} not complete` : ""}
            {cannotCheck > 0 ? ` · ${cannotCheck} cannot check` : ""}
            {toGo > 0 ? ` · ${toGo} to go` : ""}
          </span>
          <span>{pct}%</span>
        </div>
        <div className="rcmt-progress-track">
          <div className="rcmt-progress-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {allDone && (
        <div className="rcmt-done">
          <div className="rcmt-done-title">
            <PartyPopper aria-hidden size={18} /> You’re done
          </div>
          <div className="rcmt-done-sub">
            Every screen in this run is marked done — until the next review.
          </div>
        </div>
      )}

      {screens.map((s) => (
        <ScreenRow
          key={s.id}
          screenId={s.id}
          routePath={s.route_path}
          label={s.label}
          changeSummary={s.change_summary ?? null}
          jira={s.jira ?? null}
          commentCount={s.comment_count}
          outcome={s.my_outcome ?? null}
          note={s.my_note ?? null}
          token={token}
          onChanged={invalidate}
          onGo={onGo}
        />
      ))}
    </>
  );
}

function ScreenRow({
  screenId,
  routePath,
  label,
  changeSummary,
  jira,
  commentCount,
  outcome,
  note,
  token,
  onChanged,
  onGo,
}: {
  screenId: string;
  routePath: string;
  label: string;
  changeSummary: string | null;
  jira: JiraRef | null;
  commentCount: number;
  outcome: SignoffOutcome | null;
  note: string | null;
  token: string | undefined;
  onChanged: () => void;
  onGo: () => void;
}) {
  // Set my outcome (done | not_complete | cannot_check) on this screen, with an
  // optional reason note for the two kick-backs.
  const markMut = useMutation({
    mutationFn: ({ o, n }: { o: SignoffOutcome; n?: string | null }) =>
      signoffScreen(token!, screenId, o, n),
    onSuccess: () => {
      setReasonFor(null);
      setReason("");
      onChanged();
    },
  });
  // Clear my sign-off entirely (back to not-reviewed).
  const undoMut = useMutation({
    mutationFn: () => unsignoffScreen(token!, screenId),
    onSuccess: onChanged,
  });
  const busy = !token || markMut.isPending || undoMut.isPending;

  // CHP-533: which kick-back the reviewer is currently composing a reason for
  // (null = none open). Opening the inline reason input seeds it from any
  // existing note so re-marking edits in place.
  const [reasonFor, setReasonFor] = useState<"not_complete" | "cannot_check" | null>(null);
  const [reason, setReason] = useState("");
  const openReason = (o: "not_complete" | "cannot_check") => {
    setReason(outcome === o && note ? note : "");
    setReasonFor(o);
  };
  const submitReason = () => {
    if (!reasonFor) return;
    const trimmed = reason.trim();
    markMut.mutate({ o: reasonFor, n: trimmed ? trimmed : null });
  };

  const navigate = () => {
    onGo();
    // router.navigate keeps the SPA alive (the overlay re-renders against the
    // new route_path). The cycle's screens carry arbitrary route strings, so
    // `to` is widened past the typed route union (cast to the param type —
    // not `any`). Fall back to a hard nav if the path isn't a registered
    // route so the reviewer still lands on the live screen.
    const opts = { to: routePath } as Parameters<typeof router.navigate>[0];
    void router.navigate(opts).catch(() => {
      window.location.assign(routePath);
    });
  };

  const reasonLabel = reasonFor === "cannot_check" ? "Why can't this be checked?" : "What's left?";

  return (
    <div className="rcmt-screen" data-done={outcome === "done"} data-outcome={outcome ?? "none"}>
      <div className="rcmt-screen-main">
        <div className="rcmt-screen-label">
          {/* Glyphs are decorative — the outcome is conveyed textually below for
              screen readers, so state isn't carried by colour/icon alone. */}
          {outcome === "done" && (
            <span className="rcmt-check" aria-hidden>
              ✓{" "}
            </span>
          )}
          {outcome === "not_complete" && (
            <span className="rcmt-cross" aria-hidden>
              ✗{" "}
            </span>
          )}
          {outcome === "cannot_check" && (
            <span className="rcmt-block" aria-hidden>
              ⊘{" "}
            </span>
          )}
          {label}
          {outcome === "done" && <span className="rcmt-sr-only"> — done</span>}
          {outcome === "not_complete" && <span className="rcmt-sr-only"> — not complete</span>}
          {outcome === "cannot_check" && <span className="rcmt-sr-only"> — cannot check</span>}
        </div>
        {/* CHP-533: the reviewer's reason note, shown inline on the row. */}
        {note && (outcome === "not_complete" || outcome === "cannot_check") && (
          <div className="rcmt-screen-note" data-outcome={outcome}>
            {outcome === "not_complete" ? "✗ Not complete" : "⊘ Cannot check"} — {note}
          </div>
        )}
        {jira && (
          <div className="rcmt-screen-ticket">
            <a
              className="rcmt-jira-key"
              href={jira.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {jira.key} ↗
            </a>
            {jira.summary && <span className="rcmt-jira-summary">{jira.summary}</span>}
          </div>
        )}
        {changeSummary && <div className="rcmt-screen-summary">{changeSummary}</div>}
        <div className="rcmt-screen-meta">
          <span>{routePath}</span>
          <span>
            {commentCount} comment{commentCount === 1 ? "" : "s"}
          </span>
        </div>
        {/* CHP-533: inline reason input — optional. Open when a kick-back action
            is clicked; submit applies the outcome + note. */}
        {reasonFor && (
          <div className="rcmt-reason">
            <label className="rcmt-sr-only" htmlFor={`rcmt-reason-${screenId}`}>
              {reasonLabel}
            </label>
            <input
              id={`rcmt-reason-${screenId}`}
              className="rcmt-input"
              type="text"
              value={reason}
              autoFocus
              placeholder={
                reasonFor === "cannot_check"
                  ? "Optional — e.g. blocked by CHP-123"
                  : "Optional — e.g. waiting on CHP-123"
              }
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitReason();
                }
              }}
            />
            <div className="rcmt-reason-actions">
              <button
                type="button"
                className="rcmt-btn rcmt-btn-primary rcmt-btn-xs"
                disabled={busy}
                onClick={submitReason}
              >
                {reasonFor === "cannot_check" ? "Cannot check" : "Not complete"}
              </button>
              <button
                type="button"
                className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
                disabled={busy}
                onClick={() => {
                  setReasonFor(null);
                  setReason("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="rcmt-screen-actions">
        <button type="button" className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs" onClick={navigate}>
          Go
        </button>
        {outcome !== "done" && (
          <button
            type="button"
            className="rcmt-btn rcmt-btn-primary rcmt-btn-xs"
            disabled={busy}
            onClick={() => markMut.mutate({ o: "done" })}
          >
            Mark reviewed
          </button>
        )}
        {reasonFor !== "not_complete" && (
          <button
            type="button"
            className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs rcmt-btn-warn"
            disabled={busy}
            onClick={() => openReason("not_complete")}
          >
            Not complete
          </button>
        )}
        {reasonFor !== "cannot_check" && (
          <button
            type="button"
            className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs rcmt-btn-info"
            disabled={busy}
            onClick={() => openReason("cannot_check")}
          >
            Cannot check
          </button>
        )}
        {outcome !== null && (
          <button
            type="button"
            className="rcmt-btn rcmt-btn-ghost rcmt-btn-xs"
            disabled={busy}
            onClick={() => undoMut.mutate()}
          >
            Undo
          </button>
        )}
      </div>
    </div>
  );
}
